import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import useAPI from "hooks/useAPI.js";
import OrganizationService from "services/OrganizationService";
import { useSelector } from "react-redux";
import { selectHospital } from "state/slices/hospitalSlice";
import { Paper, Grid, Box } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme } from "@mui/material/styles";
import PeopleOutlineRoundedIcon from "@mui/icons-material/PeopleOutlineRounded";
import ChecklistRtlRoundedIcon from "@mui/icons-material/ChecklistRtlRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import ColumnBarChart from "components/charts/ColumnBarChart";
import PieChart from "components/charts/PieChart";
import Typography from "@mui/material/Typography";
import HospitalStatsChartsUtils from "utils/HospitalStatsChartDataUtils";
import HOSPITAL_STATS_CHART_PROPS from "utils/constants/HospitalStatsChartProps";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";

function Dashboard() {
  const theme = useTheme();
  const navigate = useNavigate();
  const focusedHospital = useSelector(selectHospital);
  const [statsData, setStatsData] = useState({
    adolescentPatientsCount: 0,
    adultPatientsCount: 0,
    childPatientsCount: 0,
    patientsCount: 0,
    completedDoctorAssessmentsCount: 0,
    completedEnrollmentSessionsCount: 0,
    last4WeeksCompletedDoctorAssessmentForms: [],
    last4WeeksCompletedEnrollmentSessions: [],
    referralSourceCounts: {},
    referralSourcePercentages: {},
  });

  const fetchHospitalStatsAPI = useAPI(
    OrganizationService.getHospitalStats,
    fetchHospitalStatsHandler,
    false
  );

  const fetchHospitalStats = useCallback(() => {
    fetchHospitalStatsAPI.execute(focusedHospital.id);
  }, [fetchHospitalStatsAPI, focusedHospital.id]);

  useEffect(() => {
    if (focusedHospital.id !== "") {
      // Fetch stats only after hospital has been set
      fetchHospitalStats();
    }

    //Set chart categories
    setLast4WeekBasedChartCategories(
      HOSPITAL_STATS_CHART_PROPS.doctorAsmtForms
    );
    setLast4WeekBasedChartCategories(
      HOSPITAL_STATS_CHART_PROPS.enrollmentSessions
    );
  }, [focusedHospital.id]);

  /* API Success Handlers */
  function fetchHospitalStatsHandler(response) {
    setStatsData(response.data);
  }

  const referralSourceCodes = HospitalStatsChartsUtils.REFERRAL_SOURCE_CODES || [];
  const referralCounts = referralSourceCodes.map(
    (code) => (statsData.referralSourceCounts && statsData.referralSourceCounts[code]) || 0
  );
  const hasAgeGroupData =
    (statsData.childPatientsCount || 0) +
    (statsData.adultPatientsCount || 0) +
    (statsData.adolescentPatientsCount || 0) >
    0;
  const hasReferralData = referralCounts.some((count) => Number(count) > 0);
  const hasDoctorAsmtSeries = (statsData.last4WeeksCompletedDoctorAssessmentForms || []).length > 0;
  const hasEnrollmentSeries = (statsData.last4WeeksCompletedEnrollmentSessions || []).length > 0;

  const doctorAsmtWeekly = useMemo(
    () =>
      HospitalStatsChartsUtils.getLast4WeeksDataSeries(
        statsData.last4WeeksCompletedDoctorAssessmentForms || []
      )?.[0]?.data || [],
    [statsData.last4WeeksCompletedDoctorAssessmentForms]
  );

  const enrollmentWeekly = useMemo(
    () =>
      HospitalStatsChartsUtils.getLast4WeeksDataSeries(
        statsData.last4WeeksCompletedEnrollmentSessions || []
      )?.[0]?.data || [],
    [statsData.last4WeeksCompletedEnrollmentSessions]
  );

  const assessmentsThisWeek = doctorAsmtWeekly[doctorAsmtWeekly.length - 1] || 0;
  const assessmentsPrevWeek = doctorAsmtWeekly[doctorAsmtWeekly.length - 2] || 0;
  const sessionsThisWeek = enrollmentWeekly[enrollmentWeekly.length - 1] || 0;
  const sessionsPrevWeek = enrollmentWeekly[enrollmentWeekly.length - 2] || 0;

  const pendingAssessments = Math.max(
    Number(statsData.patientsCount || 0) - Number(statsData.completedDoctorAssessmentsCount || 0),
    0
  );

  const quickInsights = [
    {
      label: "Sessions this week",
      value: sessionsThisWeek,
      note: getTrendText(sessionsThisWeek - sessionsPrevWeek),
    },
    {
      label: "Assessments this week",
      value: assessmentsThisWeek,
      note: getTrendText(assessmentsThisWeek - assessmentsPrevWeek),
    },
    {
      label: "Pending assessments",
      value: pendingAssessments,
      note: pendingAssessments > 0 ? "Needs follow-up" : "No pending items",
    },
  ];

  const kpiCards = [
    {
      key: "patients",
      icon: <PeopleOutlineRoundedIcon fontSize="small" />,
      label: "Total Patients",
      value: statsData.patientsCount,
      context: "Current centre",
      trend: `${statsData.childPatientsCount || 0} child, ${statsData.adolescentPatientsCount || 0} adolescent, ${statsData.adultPatientsCount || 0} adult`,
    },
    {
      key: "assessments",
      icon: <ChecklistRtlRoundedIcon fontSize="small" />,
      label: "Assessments Completed",
      value: statsData.completedDoctorAssessmentsCount,
      context: "All time",
      trend: `${assessmentsThisWeek} this week (${getTrendText(
        assessmentsThisWeek - assessmentsPrevWeek
      )})`,
    },
    {
      key: "sessions",
      icon: <EventAvailableRoundedIcon fontSize="small" />,
      label: "Sessions Completed",
      value: statsData.completedEnrollmentSessionsCount,
      context: "All time",
      trend: `${sessionsThisWeek} this week (${getTrendText(
        sessionsThisWeek - sessionsPrevWeek
      )})`,
    },
  ];

  const chartDefinitions = {
    ageGroup: {
      title: "Patients by Age Group",
      subtitle: "Current patient distribution",
      content: !hasAgeGroupData ? (
        <ChartEmptyState
          message="No age-group data yet"
          guidance="Add or update patient profiles to populate this chart."
        />
      ) : (
        <>
          <PieChart
            chartProps={HOSPITAL_STATS_CHART_PROPS.patientAgeGroup}
            dataLabels={HOSPITAL_STATS_CHART_PROPS.patientAgeGroup.dataLabels}
            data={HospitalStatsChartsUtils.getPatientAgeGroupDataSeries(statsData)}
          />
          <Box sx={{ ml: 3 }}>
            <Legend
              labels={HOSPITAL_STATS_CHART_PROPS.patientAgeGroup.dataLabels}
              colors={HOSPITAL_STATS_CHART_PROPS.patientAgeGroup.colors}
            />
          </Box>
        </>
      ),
    },
    referral: {
      title: "Referral Sources",
      subtitle: "By percentage of intake volume",
      content: !hasReferralData ? (
        <ChartEmptyState
          message="No referral source data yet"
          guidance="Capture referral details in intake forms to track acquisition channels."
        />
      ) : (
        <>
          <PieChart
            chartProps={HOSPITAL_STATS_CHART_PROPS.referralSources}
            dataLabels={HOSPITAL_STATS_CHART_PROPS.referralSources.dataLabels}
            data={HospitalStatsChartsUtils.getReferralSourcePercentageSeries(statsData)}
          />
          <Box sx={{ ml: 3 }}>
            <Legend
              labels={HOSPITAL_STATS_CHART_PROPS.referralSources.dataLabels}
              colors={HOSPITAL_STATS_CHART_PROPS.referralSources.colors}
              counts={referralCounts}
            />
          </Box>
        </>
      ),
    },
    assessments: {
      title: "Assessment Trend",
      subtitle: "Completed doctor assessments in the last 4 weeks",
      content: !hasDoctorAsmtSeries ? (
        <ChartEmptyState
          message="No assessment trend yet"
          guidance="Completed assessments will appear here week by week."
        />
      ) : (
        <ColumnBarChart
          chartProps={HOSPITAL_STATS_CHART_PROPS.doctorAsmtForms}
          data={HospitalStatsChartsUtils.getLast4WeeksDataSeries(
            statsData.last4WeeksCompletedDoctorAssessmentForms
          )}
        />
      ),
    },
    sessions: {
      title: "Session Trend",
      subtitle: "Completed enrollment sessions in the last 4 weeks",
      content: !hasEnrollmentSeries ? (
        <ChartEmptyState
          message="No session trend yet"
          guidance="Completed sessions will be reflected here over time."
        />
      ) : (
        <ColumnBarChart
          chartProps={HOSPITAL_STATS_CHART_PROPS.enrollmentSessions}
          data={HospitalStatsChartsUtils.getLast4WeeksDataSeries(
            statsData.last4WeeksCompletedEnrollmentSessions
          )}
        />
      ),
    },
  };

  if (fetchHospitalStatsAPI.status === "pending" && !fetchHospitalStatsAPI.response) {
    return (
      <Paper
        elevation={0}
        className="surface-card section-card"
        sx={{
          minHeight: 400,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Paper>
    );
  }

  return (
    <div className="page-shell">
      <div className="page-header">
        <div>
          <h1 className="page-title">Clinic Dashboard</h1>
          <p className="page-subtitle">
            Operational snapshot for patients, assessments, sessions, and referral trends.
          </p>
        </div>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            startIcon={<PersonAddAltRoundedIcon />}
            variant="contained"
            onClick={() => navigate("/hospital/emr-management/patients/add-patient")}
          >
            New Patient
          </Button>
        </Box>
      </div>

      <div className="kpi-grid">
        {kpiCards.map((card) => (
          <div className="kpi-card" key={card.key}>
            <div className="kpi-head">
              <span className="kpi-icon">{card.icon}</span>
              <div className="kpi-label">{card.label}</div>
            </div>
            <div className="kpi-value">{card.value}</div>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
              {card.context}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {card.trend}
            </Typography>
          </div>
        ))}
      </div>

      <Paper elevation={0} className="surface-card section-card">
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }}>
          <Box>
            <Typography variant="h6">Quick Insights</Typography>
            <Typography variant="body2" color="text.secondary">
              Weekly activity snapshot for sessions, assessments, and pending work.
            </Typography>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} useFlexGap flexWrap="wrap">
            {quickInsights.map((insight) => (
              <Box
                key={insight.label}
                sx={{
                  minWidth: 180,
                  border: "1px solid #dbe5ef",
                  borderRadius: 2,
                  px: 1.5,
                  py: 1.25,
                  bgcolor: "#ffffff",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {insight.label}
                </Typography>
                <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                  {insight.value}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {insight.note}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Stack>
      </Paper>

      <Paper elevation={0} className="surface-card section-card">
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <DashboardChartCard
              title={chartDefinitions.ageGroup.title}
              subtitle={chartDefinitions.ageGroup.subtitle}
              theme={theme}
            >
              {chartDefinitions.ageGroup.content}
            </DashboardChartCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <DashboardChartCard
              title={chartDefinitions.referral.title}
              subtitle={chartDefinitions.referral.subtitle}
              theme={theme}
            >
              {chartDefinitions.referral.content}
            </DashboardChartCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <DashboardChartCard
              title={chartDefinitions.assessments.title}
              subtitle={chartDefinitions.assessments.subtitle}
              theme={theme}
              chartHeight="100%"
            >
              {chartDefinitions.assessments.content}
            </DashboardChartCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <DashboardChartCard
              title={chartDefinitions.sessions.title}
              subtitle={chartDefinitions.sessions.subtitle}
              theme={theme}
              chartHeight="100%"
            >
              {chartDefinitions.sessions.content}
            </DashboardChartCard>
          </Grid>
        </Grid>
      </Paper>
    </div>
  );
}

function DashboardChartCard({ title, subtitle, children, theme, chartHeight = "350px" }) {
  return (
    <Paper
      elevation={0}
      className="dashboard-chart-card"
      sx={{
        backgroundColor: theme.palette.background.default,
        p: 2,
        height: chartHeight,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Box>
          <Typography variant="subtitle2" color={theme.palette.text.main}>
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        </Box>
      </Stack>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          height: `calc(${chartHeight} - 56px)`,
        }}
      >
        {children}
      </Box>
    </Paper>
  );
}

function setLast4WeekBasedChartCategories(chartProps) {
  chartProps.xaxis.categories =
    HospitalStatsChartsUtils.getLast4WeeksAsXaxisCategories();
}

function ChartEmptyState({ message, guidance }) {
  return (
    <Box
      sx={{
        width: "100%",
        minHeight: 200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 2,
        border: "1px dashed #cbd5e1",
        backgroundColor: "#ffffff",
        px: 2,
        textAlign: "center",
      }}
    >
      <div>
        <Typography variant="body2" color="text.secondary" fontWeight={600}>
          {message}
        </Typography>
        {guidance ? (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
            {guidance}
          </Typography>
        ) : null}
      </div>
    </Box>
  );
}

function getTrendText(delta) {
  if (delta > 0) return `up ${delta} vs last week`;
  if (delta < 0) return `down ${Math.abs(delta)} vs last week`;
  return "no change vs last week";
}

function Legend({ labels, colors, counts }) {
  if (!labels || !colors) return null;

  return (
    <>
      {labels.map((label, index) => (
        <Box
          key={label}
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 0.5,
          }}
        >
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              bgcolor: colors[index],
              mr: 1,
            }}
          />
          <Typography variant="body2" fontWeight={500} color="text.secondary">
            {label}
            {typeof counts?.[index] === "number" && <> - {counts[index]}</>}
          </Typography>
        </Box>
      ))}
    </>
  );
}

export default Dashboard;
